import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CalendarClock, Globe } from "lucide-react";
import api from "@/lib/api";
import { flag, regionName } from "@/pages/LiveTV";

const SLOT_W = 172; // px per 90-min slot
const CH_W = 210;

export default function Guide() {
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cat, setCat] = useState("");
  const [country, setCountry] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/channels/categories").then(({ data }) => setCats(data));
    api.get("/channels/countries").then(({ data }) => setCountries(data.slice(0, 14)));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/epg", { params: { category: cat, country, limit: 40 } })
      .then(({ data }) => setItems(data.items)).finally(() => setLoading(false));
  }, [cat, country]);

  const times = items[0]?.schedule || [];
  const nowIdx = times.findIndex((s) => s.live);

  // auto-scroll so the current programme is visible
  useEffect(() => {
    if (scrollRef.current && nowIdx > 1) scrollRef.current.scrollLeft = (nowIdx - 1) * SLOT_W;
  }, [nowIdx, items.length]);

  return (
    <div className="pt-24 px-4 sm:px-8 lg:px-12 pb-20 min-h-screen" data-testid="tv-guide-page">
      <div className="flex items-center gap-3 mb-2">
        <CalendarClock className="text-brand" size={28} />
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold">TV Guide</h1>
      </div>
      <p className="text-zinc-400 mb-5">A full-day schedule across the channels. Tap any programme to start watching live.</p>

      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-3 mb-2" data-testid="guide-category-filters">
        <button onClick={() => setCat("")} className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${cat === "" ? "bg-brand text-white" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>All Categories</button>
        {cats.map((c) => (
          <button key={c.value} onClick={() => setCat(c.value)} className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${cat === c.value ? "bg-brand text-white" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>{c.label}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-2"><Globe size={15} className="text-zinc-500" /><span className="text-xs text-zinc-500">Country</span></div>
      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-4 mb-4" data-testid="guide-country-filters">
        <button onClick={() => setCountry("")} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${country === "" ? "bg-white text-black" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>🌍 All</button>
        {countries.map((c) => (
          <button key={c.code} onClick={() => setCountry(c.code)} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap flex items-center gap-1.5 ${country === c.code ? "bg-white text-black" : "bg-white/10 text-zinc-300 hover:bg-white/20"}`}>
            <span className="text-base leading-none">{flag(c.code)}</span> {regionName(c.code)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-brand" size={36} /></div>
      ) : items.length === 0 ? (
        <p className="text-zinc-400 py-20 text-center">No channels for this selection.</p>
      ) : (
        <div ref={scrollRef} className="overflow-x-auto rounded-xl border border-white/10 bg-card" data-testid="guide-grid">
          <div style={{ minWidth: CH_W + times.length * SLOT_W }}>
            {/* time header */}
            <div className="flex sticky top-0 z-20 bg-[#101014] border-b border-white/10">
              <div style={{ width: CH_W }} className="shrink-0 sticky left-0 z-40 bg-[#101014] px-4 py-3 text-xs font-bold uppercase tracking-wider text-zinc-500 border-r border-white/10">Channel</div>
              {times.map((t, i) => (
                <div key={i} style={{ width: SLOT_W }} className={`shrink-0 px-3 py-3 text-xs font-semibold border-r border-white/5 ${t.live ? "text-brand" : "text-zinc-400"}`}>{t.start}</div>
              ))}
            </div>
            {/* rows */}
            {items.map((ch) => (
              <div key={ch.id} className="flex border-b border-white/5 hover:bg-white/5 group">
                <button onClick={() => nav(`/watch/${ch.id}`)} style={{ width: CH_W }}
                  className="shrink-0 sticky left-0 z-30 bg-[#141418] group-hover:bg-[#1c1c22] px-3 py-3 flex items-center gap-2 border-r border-white/10 text-left">
                  <div className="w-9 h-9 rounded bg-black/40 grid place-items-center overflow-hidden shrink-0">
                    {ch.poster ? <img src={ch.poster} alt="" className="w-full h-full object-contain" /> : <span className="text-[10px]">{flag(ch.country)}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{ch.title}</p>
                    <p className="text-[10px] text-zinc-500 truncate">{ch.quality} · {ch.country || "—"}</p>
                  </div>
                </button>
                {ch.schedule.map((s, i) => (
                  <button key={i} onClick={() => nav(`/watch/${ch.id}`)} style={{ width: SLOT_W }}
                    data-testid={`guide-slot-${ch.id}-${i}`}
                    className={`shrink-0 text-left px-3 py-3 border-r border-white/5 relative overflow-hidden transition-colors ${s.live ? "bg-brand/15 hover:bg-brand/25" : "hover:bg-white/10"}`}>
                    {s.live && <div className="absolute bottom-0 left-0 h-0.5 bg-brand" style={{ width: `${s.progress}%` }} />}
                    <p className={`text-sm truncate ${s.live ? "text-white font-semibold" : "text-zinc-300"}`}>{s.title}</p>
                    <p className="text-[10px] text-zinc-500">{s.start}–{s.end}{s.live && <span className="text-brand font-bold"> · LIVE</span>}</p>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
